import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { ChevronsLeft, ChevronsRight } from "react-feather";

// Fix, 2026-09-04 (same day as the rule below shipped): the original rule
// was a denylist ("collapse on any literal segment except edit/an id"), which
// silently mis-fired on a module's own plain landing page too - clicking a
// sidebar item lands on the index route (e.g. "/sales/orders", no extra
// segment), whose own trailing segment is the module name itself ("orders")
// - a literal, non-id, non-"edit" word, so the old rule collapsed the list
// on every single sidebar click, not just on real action routes. Rewritten
// as an allowlist of the actual action words instead: a module's own base
// path segment can never coincidentally match one of these, so the index
// route (and any other page whose last segment isn't one of these) now
// correctly stays expanded with zero special-casing needed.
const COLLAPSE_ON_SEGMENT = /^(new(-[a-z0-9-]+)?|resubmit|promote|guarding|bulk-guards|field-visits)$/i;

// Standard rule (2026-09-04): the trailing route segment decides. Only a
// segment matching a known focused-workflow action word above collapses the
// list - everything else (a module's own index/landing route, a bare id -
// :recordGuid/:vehicleId/:employeeRecordGuid, a plain "/edit") means
// "viewing/browsing," so the list stays visible. This is what let Sales
// Orders/Enquiries' "new"/"new-confirmed"/"resubmit" routes work with zero
// per-module wiring, and why every other split-view module (Products,
// Employees, Vehicles, Funds, Customers, Vouchers, Refills, Reconciliations,
// Quotations, Prototypes, Pending Deployments, Supervisor Areas) gets the
// same behavior for free.
export const isFocusedFormPath = (pathname) => {
  const segments = pathname.split("/").filter(Boolean);
  const last = segments[segments.length - 1] || "";
  return COLLAPSE_ON_SEGMENT.test(last);
};

// Reusable split-view shell for the "compact list + inline detail" pattern
// (design concept approved 2026-08-28, piloted on Employee Master before any
// wider rollout). The list pane always stays visible at roughly a third of
// the width; the detail pane is a plain react-router <Outlet/> so whichever
// module owns this layout keeps using its own normal nested routes (index =
// empty state, :recordGuid/edit = the existing form, etc.) with zero change
// to how those pages themselves are built - only the routing shell around
// them changes from "full-page navigation" to "split view."
//
// The list pane can collapse so the detail pane goes full-width - same
// mechanic as the main sidebar's own toggle (Header.jsx's toggleSidebar, a
// class swap the CSS transitions), not a new pattern. Always manually
// togglable via the boundary chevron. `autoCollapseList(pathname)` decides
// the default per route and defaults to `isFocusedFormPath` above, so most
// callers need not pass it at all - override only for a module-specific
// exception. The manual override resets whenever the route changes, so
// leaving a collapsed route restores the route's own default.
const MasterDetailLayout = ({ list, autoCollapseList = isFocusedFormPath }) => {
  const { pathname } = useLocation();
  const autoCollapsed = Boolean(autoCollapseList?.(pathname));
  const [manualCollapsed, setManualCollapsed] = useState(null);

  useEffect(() => {
    setManualCollapsed(null);
  }, [pathname]);

  const collapsed = manualCollapsed ?? autoCollapsed;

  return (
    <div className={`master-detail-shell${collapsed ? " list-collapsed" : ""}`}>
      <div className="master-detail-list-pane">{list}</div>
      <button
        type="button"
        className="master-detail-list-toggle"
        title={collapsed ? "Show list" : "Hide list"}
        onClick={() => setManualCollapsed(!collapsed)}
      >
        {collapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
      </button>
      <div className="master-detail-detail-pane">
        <Outlet />
      </div>
    </div>
  );
};

export default MasterDetailLayout;
